import { inject, Injectable } from '@angular/core';
import { AuthService } from '@camino-solutions/core/auth/service';
import type {
  ProfileSwitchRequest,
  ProfileSwitchResponse,
  RoleInfo,
} from '@camino-solutions/share/proto/common/auth';
import { Operation, ProfileRequest } from '@camino-solutions/share/proto/common/auth';
import type { Connectable, Observable } from 'rxjs';
import {
  connectable,
  lastValueFrom,
  map,
  merge,
  ReplaySubject,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { environment } from '@camino-solutions/core/environment';
import type { User } from './model/user';
import { TestRule } from '@camino-solutions/core/access-rule';
import { AppInitializedModuleAction } from '@camino-solutions/core/app/state';
import { Store } from '@ngxs/store';
import { SelfService } from './self.service';

let init = false;

@Injectable({ providedIn: 'root' })
export class UserService {
  readonly #store = inject(Store);
  readonly #selfService = inject(SelfService);
  readonly #authService = inject(AuthService);
  // readonly #userSubject$ = new BehaviorSubject<User | null>(null);
  #user$!: Connectable<User>;
  readonly #operations = new Map<Operation['value'], Operation>();
  readonly #refreshTrigger$ = new Subject<void>();

  get user$(): Observable<User> {
    return this.#user$;
  }

  refreshUser(): void {
    this.#refreshTrigger$.next();
  }

  initApp() {
    if (init === true) {
      throw '[UserService::initApp] duplicate call';
    }
    init = true;

    const getUserProfile$ = () =>
      this.#selfService
        .getProfile(new ProfileRequest({ clientId: environment.auth.clientId }))
        .pipe(
          map(profile => {
            this.#flatRawOperationCodes(profile.roles ?? []);
            this.#operations.set(
              TestRule.instance.operation,
              new Operation({
                value: TestRule.instance.operation,
                executable: false,
              }),
            );

            this.#store.dispatch(new AppInitializedModuleAction('auth'));

            return {
              profile,
              userInfo: this.#authService.identityClaims,
            } as User;
          }),
        );

    this.#user$ = connectable(
      merge(
        // Initial load
        this.#authService.canActivateProtectedRoutes$.pipe(
          take(1),
          switchMap(() => getUserProfile$()),
        ),
        // Refresh trigger
        this.#refreshTrigger$.pipe(switchMap(() => getUserProfile$())),
      ),
      {
        connector: () => new ReplaySubject<User>(1),
        resetOnDisconnect: false,
      },
    );
    this.#user$.connect();

    return lastValueFrom(this.#user$.pipe(take(1)));
  }

  hasOperation(operation: Operation['value']): boolean {
    return this.#operations.has(operation);
  }

  isExecutableOperation(operation: Operation['value']): boolean {
    // _log('isExecutableOperation: $operation => ${operations[operation]!.executable}');
    if (this.hasOperation(operation) === false) {
      throw 'Check has operation before use isExecutableOperation';
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#operations.get(operation)!.executable;
  }

  isAdvertisementOperation(operation: Operation['value']): boolean {
    if (this.hasOperation(operation) === false) {
      throw 'Check has operation before use isExecutableOperation';
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#operations.get(operation)!.advertisment;
  }

  #flatRawOperationCodes(roles: RoleInfo[]): void {
    roles.forEach(role =>
      role.operations?.forEach(operation => this.#operations.set(operation.value, operation)),
    );
  }

  switchProfile(request: ProfileSwitchRequest): Observable<ProfileSwitchResponse> {
    return this.#selfService.switchProfile(request);
  }
}
